import { BaseSeratoFilter } from '@/pixi/filters/BaseSeratoFilter'
import { EffectState } from '@/pixi/filters/types'

interface VHSUniforms {
  isAudioReactive: boolean,
  audioIntensity: number,
  intensity: number
}

export class VHSFilter extends BaseSeratoFilter {
  
  get uniforms (): VHSUniforms {
    return super.uniforms as VHSUniforms
  }

  constructor (effectState: EffectState) {
    const shader = `
    #define reactivityIntensity 0.5
    #define VHSRES vec2(320.0, 240.0)
    #define RES vec2(1920.0, 1080.0)
    #define NUMBEROFSAMPLES 6
    #define BLURRYNESS 4.0

    varying vec2 vTextureCoord;
    uniform sampler2D uSampler;
    uniform float timeElapsed;
    uniform float intensity;
    
    uniform bool isAudioReactive;
    uniform float audioIntensity;

    // Book of shaders, uniform distributed random number generator
    float random(vec2 st) {
      return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
    }

    mat2 rotate2D(float r) {
      return mat2(cos(r), sin(r), -sin(r), cos(r));
    }

    vec3 rgb2yiq(vec3 rgb) {
      return mat3(0.2989, 0.5959, 0.2115, 0.5870, -0.2744, -0.5229, 0.1140, -0.3216, 0.3114) * rgb;
    }

    vec3 yiq2rgb(vec3 yiv) {
      return mat3(1.0001, 0.9959, 0.95113, 1.000, -0.2719, -1.0470, 0.9999, -1.1060, 1.7029) * yiv;
    }

    vec3 generateNoise(vec2 n) 
    {
      float nrnd0 = random(n + 0.07);
      float nrnd1 = random(n + 0.11);	
      float nrnd2 = random(n + 0.13);
      float nrnd3 = random(n + 0.17);
      return vec3((nrnd2+nrnd3) / 2.0, nrnd0, nrnd1);
    }

    vec3 getTexture(vec2 uv, float colourShift) {
      vec3 yiq = vec3(0.0);
      for (int i = 0; i < NUMBEROFSAMPLES; i++) {
        yiq += (
          rgb2yiq(texture2D(uSampler, uv - (vec2(float(i), 0.0) / VHSRES.x)
          * (vec2(float(NUMBEROFSAMPLES - i)) / 2.0)
          * (vec2(float(i)) / BLURRYNESS)
          / 1.1
          ).xyz / float(NUMBEROFSAMPLES))
        );
      }
      if (colourShift != 0.0) { 
        yiq.yz = rotate2D(colourShift) * yiq.yz;
      }
      return yiq2rgb(yiq);
    }

    void main(void)
    {
      // Make sure multipliers are not 0
      float intensityMultiplierExponential = ((1.1/(1.1 - intensity) - 1.0) * 1.2) + 0.1;
      float intensityMultiplierLinear = (intensity + 0.1) / 2.0;
      if (isAudioReactive) {
        intensityMultiplierExponential *= audioIntensity;
        intensityMultiplierLinear *= audioIntensity;
        // ensure these multipliers are not zero
        intensityMultiplierExponential += 0.01;
        intensityMultiplierLinear += 0.01;
      }
      float time = timeElapsed * -1.0;
      vec2 vhsResRatio = VHSRES / RES;
      vec2 uv = vTextureCoord;
      uv = vTextureCoord.xy / vhsResRatio;
      vec4 originalPixelColor = texture2D(uSampler, uv);
      vec2 uvn = uv;

      // tape wave
      uvn.x += generateNoise(vec2(uvn.x, time)).x / VHSRES.x;
      uvn.x += generateNoise(vec2(uvn.y, time * 8.0)).x / VHSRES.x;

      // create crease modifiers
      float phase = smoothstep(0.9, 1.0, sin(6.0 * uvn.y - 2.0 * (time + generateNoise(time * uvn).x / 3.0)));
      float noise = smoothstep(0.0, 1.0, generateNoise(vec2(uv.y, time)).x);

      // tape crease
      float tc = phase * noise;
      uvn.x = uvn.x - intensityMultiplierExponential * tc / VHSRES.x * 6.0;

      // switching noise adds a small strip of condensed texture at the top. colourShift is set to control the colour shift that occurs for this area.
      float colourShift = 0.0;
      if (uvn.y < 0.012 * intensityMultiplierLinear * 2.0) {
        colourShift = -15.5;
        uvn.y *= 40.0;
        uvn.x += uvn.x / VHSRES.x * 6.0;
        uvn.x += (generateNoise(vec2(uvn.y, time)).x * 10.0 / VHSRES.x);
      }

      vec3 tex = vec3(0.0);

      // Ensure that uvn is within the texture bounds
      if (uvn.x < 0.0) {
        uvn.x += 1.0;
      }

      tex = getTexture(uvn, colourShift);
      //crease noise
      float creaseNoise = noise * (0.3 + 0.7 * phase);
      // reduce x resolution
      vec2 uvp = vec2(floor(uvn.x * 50.0), uvn.y * 50.0) / 50.0;
      vec3 xNoise = generateNoise(vec2(uvp.x, time));
      vec3 yNoise = generateNoise(uvp.yy);
      if (creaseNoise > 0.3) {
        // phase above 0.3 indicates that we are looking at the tape crease
        xNoise = abs(xNoise - yNoise / 4.0);
        tex = mix(tex, generateNoise(vec2(uvp.x, time)), pow(xNoise.x, 8.0 / intensityMultiplierExponential));
      } else if (creaseNoise > 0.29) {
        // This small 0.01 phase area should create sparce randomly placed noise
        vec2 uvt = (uvp + vec2(1.0, 0.0) * yNoise.x) * vec2(0.1, 2.0);
        tex = mix(tex, 2.0 * generateNoise(uvt), pow(xNoise.x, 40.0 / intensityMultiplierExponential));
      }

      // ac beat
      tex *= 1.0 + smoothstep(0.2, 0.5, generateNoise(vec2(uv.y, time))) / 84.0;
    
      // saturate
      tex = clamp(tex, 0.0, 1.0);

      // yiq
      tex = rgb2yiq(tex);
      tex = vec3(0.05, -0.1, 0.02) + vec3(0.95, 0.9, 0.9) * tex;
      tex = yiq2rgb(tex);
      tex.b -= 0.03;

      gl_FragColor = originalPixelColor.a == 0.0 ? vec4(0.0) : vec4(tex, originalPixelColor.a);
    }`

    const uniforms: VHSUniforms = {
      isAudioReactive: false,
      audioIntensity: 0,
      intensity: 0.5
    }

    super(
      effectState,
      undefined,
      shader,
      uniforms
    )
  }

  updateUniforms () {
    this.uniforms.intensity = this.effectState.intensity
    this.uniforms.audioIntensity = this.getAudioIntensityValue()
    this.uniforms.isAudioReactive = this.effectState.audioReactivityEnabled
  }
}
